import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { useUnreadNotifier } from "@/hooks/useUnreadNotifier";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationsBell from "@/components/NotificationsBell";
import InstallPrompt from "@/components/InstallPrompt";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import {
  LayoutDashboard, MessageSquare, Inbox, Building2, Tag, Megaphone,
  TrendingUp, Package, Truck, Tablet, Settings as SettingsIcon,
  Users, BookUser, LogOut, ChevronDown, ChevronRight, Mail, FileText,
  ShieldCheck, Menu, X, Receipt,
} from "lucide-react";
import { TESTIDS } from "@/constants/testIds";

// Top-level (above sections)
const TOP_NAV = [
  { to: "/", end: true, label: "Dashboard", icon: LayoutDashboard,
    testid: TESTIDS.sbDashboard, roles: ["admin", "user", "warehouse"], page: "dashboard" },
  { to: "/messages", label: "Internal Chat", icon: MessageSquare,
    testid: "sidebar-messages-link", roles: ["admin", "user", "admin_reseller", "warehouse"], page: "messages" },
];

const SECTIONS = [
  {
    title: "Admin",
    items: [
      { to: "/purchase-orders", label: "Purchase Orders Inbox", icon: Inbox,
        testid: "sidebar-po-link", roles: ["admin", "user"], page: "purchase-orders" },
      { to: "/invoicing", label: "Invoicing", icon: Receipt,
        testid: "sidebar-invoicing-link", roles: ["admin"], page: "invoicing" },
      { to: "/companies", label: "Companies", icon: Building2,
        testid: TESTIDS.sbCompanies, roles: ["admin", "user", "admin_reseller"], page: "companies" },
      { to: "/contacts", label: "Contacts", icon: BookUser,
        testid: "sidebar-contacts-link", roles: ["admin", "user", "admin_reseller"], page: "contacts" },
      { to: "/marketing", label: "Marketing", icon: Megaphone,
        testid: "sidebar-marketing-link", roles: ["admin"], page: "marketing" },
    ],
  },
  {
    title: "Production Workflow",
    items: [
      { to: "/pipeline", label: "Orders Pipeline", icon: TrendingUp,
        testid: TESTIDS.sbPipeline, roles: ["admin", "user", "admin_reseller"], page: "pipeline" },
      { to: "/inventory", label: "Inventory", icon: Package,
        testid: TESTIDS.sbInventory, roles: ["admin", "user", "warehouse"], page: "inventory" },
      { to: "/dockets", label: "Dockets", icon: Truck,
        testid: TESTIDS.sbDockets, roles: ["admin", "user", "warehouse"], page: "dockets" },
      { to: "/yard/stock-update", label: "Yard Tablet", icon: Tablet,
        testid: "sidebar-yard-tablet", roles: ["admin", "user", "warehouse"], page: "inventory" },
    ],
  },
];

const SETTINGS_GROUP = {
  title: "Settings",
  icon: SettingsIcon,
  roles: ["admin"],
  items: [
    { to: "/settings", label: "General Settings", icon: SettingsIcon, testid: "sidebar-settings-link" },
    { to: "/settings/user-management", label: "User Management", icon: ShieldCheck, testid: "sidebar-user-management-link" },
  ],
};

function roleBadgeClass(role) {
  switch (role) {
    case "admin": return "es-badge es-badge-brand";
    case "warehouse": return "es-badge es-badge-info";
    case "reseller":
    case "admin_reseller": return "es-badge es-badge-neutral";
    case "suspended": return "es-badge es-badge-error";
    default: return "es-badge es-badge-neutral";
  }
}

function NavItem({ item, badge }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.testid}
      className={({ isActive }) =>
        [
          "flex items-center gap-3 mx-3 px-3 py-2 text-sm rounded-lg transition-all",
          isActive
            ? "bg-brand-50 text-brand-800 font-semibold ring-1 ring-brand-200"
            : "text-zinc-700 hover:bg-zinc-50",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-brand-600" : "text-zinc-500"}`} />
          <span className="truncate flex-1">{item.label}</span>
          {badge > 0 && (
            <span
              data-testid={`${item.testid}-badge`}
              className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold"
            >
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith("/settings") || location.pathname.startsWith("/marketing")
  );
  const [readyToInvoiceCount, setReadyToInvoiceCount] = useState(0);
  const [toInvoiceCount, setToInvoiceCount] = useState(0);
  const [poReviewCount, setPoReviewCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadByChannel, setUnreadByChannel] = useState({});
  const [allowedPages, setAllowedPages] = useState(null); // null = not loaded; admins ignore.
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close mobile drawer on route change.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Load per-role page-access permissions so we can filter the sidebar.
  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") { setAllowedPages(null); return; }
    let active = true;
    api.get("/role-permissions")
      .then(({ data }) => { if (active) setAllowedPages(new Set(data.permissions?.[user.role] || [])); })
      .catch(() => { if (active) setAllowedPages(new Set()); });
    return () => { active = false; };
  }, [user]);

  // Poll orders count every 30s so admins see the "Ready to Invoice" badge in near-real-time.
  useEffect(() => {
    if (!user || !["admin", "user", "admin_reseller"].includes(user.role)) return;
    let active = true;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/orders");
        if (!active) return;
        setReadyToInvoiceCount(data.filter((o) => o.Order_Status === "To Invoice").length);
        setToInvoiceCount(data.filter((o) => o.Order_Status === "To Invoice").length);
      } catch { /* ignore */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  // Poll PO inbox count every 30s so staff see new POs awaiting review without
  // refreshing. Counts only non-archived POs in the "needs attention" statuses
  // — matches what the Review tab shows on the PO Inbox page.
  useEffect(() => {
    if (!user || !["admin", "user"].includes(user.role)) return;
    let active = true;
    const fetchPos = async () => {
      try {
        const { data } = await api.get("/purchase-orders");
        if (!active) return;
        const review = data.filter((p) =>
          !p.Archived &&
          (p.Status === "parsed" || p.Status === "new" || p.Status === "parsing" || p.Status === "parse_error")
        ).length;
        setPoReviewCount(review);
      } catch { /* ignore */ }
    };
    fetchPos();
    const t = setInterval(fetchPos, 30000);
    return () => { active = false; clearInterval(t); };
  }, [user, location.pathname]);

  // Poll unread chat-message count every 20s — drives the badge on Internal Chat
  useEffect(() => {
    if (!user) return;
    let active = true;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (!active) return;
        setUnreadMessages(data.total || 0);
        setUnreadByChannel(data.per_channel || {});
      } catch { /* ignore — endpoint may not exist on older deployments */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 20000);
    // also refetch when route changes (likely user just visited the chat)
    return () => { active = false; clearInterval(t); };
  }, [user, location.pathname]);

  // Drive the tab title, favicon dot, and desktop notification off the
  // unread chat-message count. Permission for desktop notifications must
  // be requested separately from the user's profile.
  useUnreadNotifier(unreadMessages, "Edenstone Ops");
  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const allowed = (item) => {
    if (!item.roles.includes(user.role)) return false;
    if (user.role === "admin") return true;
    if (allowedPages === null) return false; // hide until permissions resolve (no FOUC)
    if (!item.page) return true;
    return allowedPages.has(item.page);
  };
  const visibleTop = TOP_NAV.filter(allowed);
  const showSettings = SETTINGS_GROUP.roles.includes(user.role);

  return (
    <div className="min-h-screen flex bg-zinc-50">
      {/* Backdrop (mobile only) */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={[
          "bg-white border-r border-zinc-200 flex flex-col z-50",
          // Mobile: off-canvas drawer
          "fixed inset-y-0 left-0 w-72 transform transition-transform duration-200 ease-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          // Desktop: in-flow, fixed width
          "lg:static lg:translate-x-0 lg:w-64",
        ].join(" ")}
        data-testid="app-sidebar"
      >
        <div className="px-5 py-5 border-b border-zinc-100 flex items-center gap-3">
          <img src="/edenstone-logo.png" alt="Edenstone Masonry" className="w-14 h-14 object-contain bg-white rounded-md p-1 ring-1 ring-zinc-200" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-zinc-900 text-base leading-none tracking-tight">EDENSTONE</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">Masonry Ops</div>
          </div>
          <button
            data-testid="sidebar-close-btn"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-zinc-500 hover:text-zinc-900 p-1"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {/* Top */}
          <div className="space-y-1">
            {visibleTop.map((it) => (
              <NavItem
                key={it.to}
                item={it}
                badge={
                  it.to === "/messages" ? unreadMessages :
                  it.to === "/invoicing" ? toInvoiceCount :
                  0
                }
              />
            ))}
          </div>

          {/* Sections */}
          {SECTIONS.map((section) => {
            const items = section.items.filter(allowed);
            if (items.length === 0) return null;
            return (
              <div key={section.title} className="mt-5">
                <div className="px-6 mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">
                  {section.title}
                </div>
                <div className="space-y-1">
                  {items.map((it) => (
                    <NavItem
                      key={it.to}
                      item={it}
                      badge={
                        it.to === "/invoicing" ? toInvoiceCount :
                        it.to === "/purchase-orders" ? poReviewCount :
                        0
                      }
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Settings (collapsible) */}
          {showSettings && (
            <div className="mt-5">
              <button
                onClick={() => setSettingsOpen((s) => !s)}
                data-testid="sidebar-settings-toggle"
                className={[
                  "w-full flex items-center gap-3 mx-3 px-3 py-2 text-sm rounded-lg transition-colors",
                  settingsOpen
                    ? "bg-brand-50 text-brand-800 font-semibold ring-1 ring-brand-200"
                    : "text-zinc-700 hover:bg-zinc-50",
                ].join(" ")}
                style={{ width: "calc(100% - 1.5rem)" }}
              >
                <SettingsIcon className={`w-4 h-4 flex-shrink-0 ${settingsOpen ? "text-brand-600" : "text-zinc-500"}`} />
                <span className="flex-1 text-left">Settings</span>
                {settingsOpen
                  ? <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />
                  : <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />}
              </button>
              {settingsOpen && (
                <div className="mt-1 space-y-0.5 pl-3">
                  <div className="px-6 my-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400 flex items-center gap-1">
                    <ChevronDown className="w-3 h-3" /> Configuration
                  </div>
                  {SETTINGS_GROUP.items.map((it) => (
                    <NavLink
                      key={it.to + it.label}
                      to={it.to}
                      data-testid={it.testid}
                      className={({ isActive }) =>
                        [
                          "flex items-center gap-3 mx-3 pl-9 pr-3 py-1.5 text-sm rounded-lg transition-colors",
                          isActive
                            ? "text-brand-700 font-semibold"
                            : "text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50",
                        ].join(" ")
                      }
                    >
                      {it.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* User footer */}
        <div className="border-t border-zinc-100 px-4 py-4">
          <NavLink
            to="/profile"
            data-testid="sidebar-profile-link"
            className={({ isActive }) => [
              "flex items-center gap-3 mb-3 px-1 py-1 rounded-lg -mx-1 transition-colors hover:bg-zinc-50",
              isActive ? "bg-zinc-50" : "",
            ].join(" ")}
            title="Edit my profile & email signature"
          >
            <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center font-semibold text-sm rounded-lg">
              {user.full_name?.[0] || user.email[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-zinc-900 truncate">{user.full_name || user.email}</div>
              <span className={roleBadgeClass(user.role)}>{user.role}</span>
            </div>
          </NavLink>
          <button
            data-testid={TESTIDS.logoutBtn}
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 h-9 text-sm font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
          <InstallPrompt />
        </div>
      </aside>

      {/* Main area */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4 lg:px-8 gap-3 lg:gap-6">
          <button
            data-testid="sidebar-open-btn"
            onClick={() => setMobileOpen(true)}
            className="lg:hidden text-zinc-700 hover:text-zinc-900 p-1 flex-shrink-0"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0"><GlobalSearch /></div>
          <div className="flex items-center gap-4 text-xs text-zinc-500 flex-shrink-0">
            <NotificationsBell />
            <span className="hidden md:inline">Signed in as <span className="font-medium text-zinc-900">{user.email}</span></span>
            <span className="hidden md:inline text-zinc-300">·</span>
            <span className="hidden md:inline text-zinc-400">Edenstone Ops v1.0</span>
          </div>
        </header>
        <div className="p-4 sm:p-6 lg:p-8 animate-fade-in">
          <Outlet />
        </div>
      </main>
      {user.must_change_password && <ChangePasswordModal />}
    </div>
  );
}
